/**
 * VisitorRegistrationForm - Form component for registering new visitors
 * Includes photo capture, document upload, and offline support
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Camera,
  Upload,
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle,
  Loader,
  RotateCcw,
  Download,
  Eye,
  Trash2
} from 'lucide-react';
import useVisitorData from './hooks/useVisitorData';
import useVisitorPermissions from './hooks/useVisitorPermissions';
import useVisitorOffline from './hooks/useVisitorOffline';

const VisitorRegistrationForm = ({ onClose, onSuccess, editingVisitor = null }) => {
  const isEditing = !!editingVisitor;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: editingVisitor?.first_name || '',
    last_name: editingVisitor?.last_name || '',
    email: editingVisitor?.email || '',
    phone: editingVisitor?.phone || '',
    company: editingVisitor?.company || '',
    purpose: editingVisitor?.purpose || '',
    property_id: editingVisitor?.property_id || '',
    host_id: editingVisitor?.host_id || '',
    host_email: editingVisitor?.host_email || '',
    expected_duration: editingVisitor?.expected_duration || '60',
    visit_date: editingVisitor?.visit_date || new Date().toISOString().split('T')[0],
    notes: editingVisitor?.notes || ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [photoData, setPhotoData] = useState(editingVisitor?.photo_url || null);
  const [cameraActive, setCameraActive] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  
  // Custom hooks
  const { registerVisitor, isOffline } = useVisitorData();
  const { permissions, getAccessibleBuildings } = useVisitorPermissions();
  const { addPendingAction } = useVisitorOffline();
  
  // Mock buildings data (in real app, this would come from API)
  const [buildings] = useState([
    { id: '1', name: 'Main Building', address: '123 Main St' },
    { id: '2', name: 'North Tower', address: '456 North Ave' },
    { id: '3', name: 'South Wing', address: '789 South Blvd' }
  ]);
  
  const accessibleBuildings = getAccessibleBuildings(buildings);
  
  // Purpose options
  const purposeOptions = [
    'Business Meeting',
    'Interview',
    'Delivery',
    'Maintenance',
    'Consultation',
    'Training',
    'Event',
    'Other'
  ];
  
  // Duration options (in minutes)
  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '120', label: '2 hours' },
    { value: '240', label: '4 hours' },
    { value: '480', label: '8 hours' },
    { value: 'custom', label: 'Custom' }
  ];
  
  // Initialize camera with fallback logic
  const startCamera = async () => {
    try {
      console.log('Starting visitor camera');
      
      // First, set the camera state to render the video element
      setCameraActive(true);
      
      // Wait for the next tick to ensure video element is rendered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Try back camera first (environment)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'environment' // Back camera
          } 
        });
        console.log('Back camera stream obtained');
      } catch (backCameraError) {
        console.log('Back camera not available, trying front camera...');
        // Fallback to front camera
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user' // Front camera
          } 
        });
        console.log('Front camera stream obtained');
      }
      
      // Retry mechanism for video ref
      let retryCount = 0;
      const maxRetries = 10;
      
      const assignStream = () => {
        if (videoRef.current && stream) {
          console.log('Assigning stream to visitor video element');
          
          const video = videoRef.current;
          
          const handleLoadedMetadata = () => {
            console.log('Visitor video metadata loaded, camera ready');
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          };
          
          const handleError = (e) => {
            console.error('Visitor video error:', e);
            video.removeEventListener('error', handleError);
          };
          
          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          video.addEventListener('error', handleError);
          
          // Assign stream to video element
          video.srcObject = stream;
          video.muted = true; // Required for autoplay in some browsers
          
        } else if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Visitor video ref not ready, retrying... (${retryCount}/${maxRetries})`);
          setTimeout(assignStream, 50);
        } else {
          console.error('Visitor video ref not available after retries, stopping camera');
          // Stop the stream if we can't assign it
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
          setCameraActive(false);
          alert('Unable to initialize camera. Please try again or use file upload instead.');
        }
      };
      
      assignStream();
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraActive(false);
      alert('Unable to access camera. Please check permissions and ensure your device has a camera.');
    }
  };
  
  // Stop camera
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  };
  
  // Capture photo
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setPhotoData(photoDataUrl);
      stopCamera();
    }
  };
  
  // Handle file upload for photo
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 64 * 1024 * 1024) { // 64MB limit
        alert('Photo size must be less than 64MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoData(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle document upload
  const handleDocumentUpload = (event) => {
    const files = Array.from(event.target.files);
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type
    }));
    setDocuments(prev => [...prev, ...newDocuments]);
  };
  
  // Remove document
  const removeDocument = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.purpose.trim()) newErrors.purpose = 'Purpose of visit is required';
    if (!formData.property_id) newErrors.property_id = 'Building selection is required';
    if (!formData.host_id.trim()) newErrors.host_id = 'Host ID is required';
    if (!formData.visit_date) newErrors.visit_date = 'Visit date is required';
    
    // Validate visit date is not in the past
    const visitDate = new Date(formData.visit_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (visitDate < today) {
      newErrors.visit_date = 'Visit date cannot be in the past';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const visitorData = {
        ...formData,
        photo_data: photoData,
        documents: documents.map(doc => ({
          name: doc.name,
          size: doc.size,
          type: doc.type,
          data: doc.file // In real app, this would be uploaded to storage
        })),
        status: 'pending',
        created_at: new Date().toISOString()
      };
      
      if (isOffline) {
        // Handle offline registration
        await addPendingAction('REGISTER_VISITOR', visitorData);
        onSuccess?.({
          ...visitorData,
          id: `offline_${Date.now()}`,
          is_offline: true
        });
      } else {
        // Online registration
        const result = await registerVisitor(visitorData);
        onSuccess?.(result);
      }
      
    } catch (error) {
      console.error('Error registering visitor:', error);
      alert('Failed to register visitor. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Visitor' : 'Register New Visitor'}
            {isOffline && (
              <span className="ml-2 inline-flex items-center gap-1 text-sm text-orange-600">
                <AlertTriangle className="w-4 h-4" />
                Offline Mode
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Photo Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Visitor Photo</h3>
            
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Photo Preview */}
              <div className="flex-1">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {photoData ? (
                    <div className="space-y-4">
                      <img
                        src={photoData}
                        alt="Visitor photo"
                        className="mx-auto max-w-full h-48 object-cover rounded-lg"
                      />
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowPreview(true)}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          <Eye className="w-4 h-4" />
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => setPhotoData(null)}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Camera className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="text-gray-500">No photo captured</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Camera Controls */}
              <div className="flex-1">
                {cameraActive ? (
                  <div className="space-y-4">
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-48 object-cover"
                      />
                      {/* Camera overlay with viewfinder */}
                      <div className="absolute inset-0 pointer-events-none">
                        {/* Viewfinder frame */}
                        <div className="absolute inset-4 border-2 border-white border-opacity-50 rounded-lg">
                          {/* Corner guides */}
                          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-white"></div>
                          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-white"></div>
                          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-white"></div>
                          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-white"></div>
                        </div>
                        {/* Center crosshair */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div className="w-6 h-6 border border-white border-opacity-75 rounded-full">
                            <div className="w-2 h-2 bg-white bg-opacity-75 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                          </div>
                        </div>
                      </div>
                      {/* Camera controls overlay */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3">
                        <button
                          type="button"
                          onClick={capturePhoto}
                          className="w-16 h-16 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 transform hover:scale-105"
                        >
                          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                            <Camera className="w-6 h-6 text-white" />
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          className="w-12 h-12 bg-red-600 bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
                        >
                          <X className="w-6 h-6 text-white" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 text-center">Position your face within the frame and click capture</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Camera className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={startCamera}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Camera className="w-4 h-4" />
                          Take Photo
                        </button>
                        <p className="text-sm text-gray-500">or</p>
                        <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <Upload className="w-4 h-4" />
                          Upload Photo
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <canvas ref={canvasRef} className="hidden" />
          </div>
          
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.first_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                </div>
                {errors.first_name && <p className="text-sm text-red-600 mt-1">{errors.first_name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.last_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                </div>
                {errors.last_name && <p className="text-sm text-red-600 mt-1">{errors.last_name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter email address"
                  />
                </div>
                {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter phone number"
                  />
                </div>
                {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company/Organization *
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.company ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter company name"
                />
                {errors.company && <p className="text-sm text-red-600 mt-1">{errors.company}</p>}
              </div>
            </div>
          </div>
          
          {/* Visit Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Visit Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose of Visit *
                </label>
                <select
                  value={formData.purpose}
                  onChange={(e) => handleInputChange('purpose', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.purpose ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select purpose</option>
                  {purposeOptions.map(purpose => (
                    <option key={purpose} value={purpose}>{purpose}</option>
                  ))}
                </select>
                {errors.purpose && <p className="text-sm text-red-600 mt-1">{errors.purpose}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    value={formData.property_id}
                    onChange={(e) => handleInputChange('property_id', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.property_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select building</option>
                    {accessibleBuildings.map(building => (
                      <option key={building.id} value={building.id}>
                        {building.name} - {building.address}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.property_id && <p className="text-sm text-red-600 mt-1">{errors.property_id}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Host ID *
                </label>
                <input
                  type="text"
                  value={formData.host_id}
                  onChange={(e) => handleInputChange('host_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.host_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter host ID"
                />
                {errors.host_id && <p className="text-sm text-red-600 mt-1">{errors.host_id}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Host Email
                </label>
                <input
                  type="email"
                  value={formData.host_email}
                  onChange={(e) => handleInputChange('host_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter host email (optional)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visit Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    value={formData.visit_date}
                    onChange={(e) => handleInputChange('visit_date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.visit_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.visit_date && <p className="text-sm text-red-600 mt-1">{errors.visit_date}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Duration
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    value={formData.expected_duration}
                    onChange={(e) => handleInputChange('expected_duration', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {durationOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional information or special requirements..."
              />
            </div>
          </div>
          
          {/* Documents Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Documents (Optional)</h3>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <FileText className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Upload Documents
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 5MB each)
                </p>
              </div>
              
              {documents.length > 0 && (
                <div className="mt-4 space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(doc.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Updating...' : 'Registering...'}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {isEditing ? 'Update Visitor' : 'Register Visitor'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {/* Photo Preview Modal */}
      {showPreview && photoData && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg p-4 max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Photo Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <img
              src={photoData}
              alt="Visitor photo preview"
              className="max-w-full h-auto rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorRegistrationForm;